import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import CommentSection from './components/CommentSection';

const IndividualBlogPostPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    if (slug) {
      fetchBlogPost();
    }
  }, [slug]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase?.from('blogs')?.select('*')?.eq('slug', slug)?.eq('status', 'published')?.single();

      if (fetchError) throw fetchError;

      setArticle({
        id: data?.id,
        title: data?.title,
        category: data?.category,
        author: data?.author_name,
        authorRole: data?.author_role,
        date: new Date(data?.published_at)?.toISOString()?.split('T')?.[0],
        readTime: data?.read_time,
        image: data?.featured_image,
        content: data?.content?.blocks || [],
        slug: data?.slug,
        metaTitle: data?.meta_title,
        metaDescription: data?.meta_description,
        keywords: data?.keywords,
        ogImage: data?.og_image,
        schemaType: data?.schema_type || 'Article',
        publishedAt: data?.published_at,
        excerpt: data?.excerpt
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError(err?.message || 'Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (block) => {
    if (block?.type === 'html') {
      return <div dangerouslySetInnerHTML={{ __html: block?.content }} className="prose prose-sm max-w-none" />;
    }
    if (block?.type === 'paragraph') {
      return <p className="text-foreground leading-relaxed mb-6">{block?.content || block?.text}</p>;
    }
    if (block?.type === 'heading') {
      const HeadingTag = `h${block?.level || 2}`;
      return <HeadingTag className="text-2xl font-bold text-foreground mb-4 mt-8">{block?.content || block?.text}</HeadingTag>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Blog Post Not Found | FireCaseIQ</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <header className="bg-card border-b border-border py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
            <button onClick={() => navigate('/blog')} className="flex items-center">
              <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
            </button>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-12 text-center">
          <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-error" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Blog Post Not Found</h1>
          <p className="text-muted-foreground mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/blog')}
            className="text-primary hover:underline"
          >
            ← Back to Blog
          </button>
        </div>
      </div>
    );
  }

  // Generate structured data for schema markup
  const structuredData = {
    "@context": "https://schema.org",
    "@type": article?.schemaType || "Article",
    "headline": article?.metaTitle || article?.title,
    "description": article?.metaDescription || article?.excerpt,
    "image": article?.ogImage || article?.image,
    "author": {
      "@type": "Person",
      "name": article?.author,
      "jobTitle": article?.authorRole
    },
    "publisher": {
      "@type": "Organization",
      "name": "FireCaseIQ",
      "logo": {
        "@type": "ImageObject",
        "url": `${window?.location?.origin}/favicon.ico`
      }
    },
    "datePublished": article?.publishedAt,
    "dateModified": article?.publishedAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": window?.location?.href
    },
    "keywords": article?.keywords?.join(', ')
  };

  const pageUrl = window?.location?.href;
  const siteTitle = "FireCaseIQ";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{article?.metaTitle || article?.title} | {siteTitle}</title>
        <meta name="title" content={article?.metaTitle || article?.title} />
        <meta name="description" content={article?.metaDescription || article?.excerpt} />
        {article?.keywords && article?.keywords?.length > 0 && (
          <meta name="keywords" content={article?.keywords?.join(', ')} />
        )}
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={article?.metaTitle || article?.title} />
        <meta property="og:description" content={article?.metaDescription || article?.excerpt} />
        {(article?.ogImage || article?.image) && (
          <meta property="og:image" content={article?.ogImage || article?.image} />
        )}
        <meta property="og:site_name" content={siteTitle} />
        <meta property="article:published_time" content={article?.publishedAt} />
        <meta property="article:author" content={article?.author} />
        {article?.keywords?.map((keyword, index) => (
          <meta key={index} property="article:tag" content={keyword} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={article?.metaTitle || article?.title} />
        <meta name="twitter:description" content={article?.metaDescription || article?.excerpt} />
        {(article?.ogImage || article?.image) && (
          <meta name="twitter:image" content={article?.ogImage || article?.image} />
        )}
        
        {/* Canonical URL */}
        <link rel="canonical" href={pageUrl} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      
      <header className="bg-card border-b border-border py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between">
          <button onClick={() => navigate('/blog')} className="flex items-center">
            <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-10 object-contain" />
          </button>
          <button
            onClick={() => navigate('/blog')}
            className="text-sm text-muted-foreground hover:text-primary transition-smooth"
          >
            ← Back to Blog
          </button>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-12">
        <article>
          <div className="mb-8">
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
              <span className="px-3 py-1 bg-primary text-primary-foreground rounded-full font-medium">
                {article?.category?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ')}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Calendar" size={14} />
                {new Date(article?.date)?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
              <span className="flex items-center gap-1">
                <Icon name="Clock" size={14} />
                {article?.readTime}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              {article?.title}
            </h1>

            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
                <span className="text-white font-semibold">
                  {article?.author?.split(' ')?.map(n => n?.[0])?.join('')}
                </span>
              </div>
              <div>
                <p className="font-medium text-foreground">{article?.author}</p>
                <p className="text-sm text-muted-foreground">{article?.authorRole}</p>
              </div>
            </div>

            {article?.image && (
              <img
                src={article?.image}
                alt={article?.title}
                className="w-full h-96 object-cover rounded-xl mb-8"
              />
            )}
          </div>

          <div className="prose prose-lg max-w-none">
            {article?.content?.map((block, index) => (
              <div key={index}>{renderContent(block)}</div>
            ))}
          </div>
        </article>

        <CommentSection blogId={article?.id} blogSlug={article?.slug} />
      </main>
    </div>
  );
};

export default IndividualBlogPostPage;